import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from './types';

// Token getter function - will be set by the auth provider
let getTokenFn: (() => string | null) | null = null;
let onUnauthorizedFn: (() => void) | null = null;

/**
 * Set the token getter function (called by auth provider)
 */
export function setTokenGetter(fn: () => string | null): void {
  getTokenFn = fn;
}

/**
 * Set the unauthorized handler (called by auth provider)
 */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorizedFn = fn;
}

/**
 * Create the axios instance
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - inject auth token
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (getTokenFn) {
      const token = getTokenFn();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor - handle errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse>) => {
    if (error.response?.status === 401) {
      // Unauthorized - trigger re-login
      onUnauthorizedFn?.();
    }
    
    // Extract error message from response
    const message = error.response?.data?.message 
      || error.message 
      || 'An error occurred';
    
    return Promise.reject(new Error(message));
  }
);

export default apiClient;
